'use client'
import React, { useState, useRef, useEffect } from 'react'
import styled, { keyframes } from 'styled-components'
import { createPortal } from 'react-dom'
import MouseIcon from '@mui/icons-material/Mouse'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import { Tooltip } from '@mui/material'
import { useConfig } from '@/contexts/ConfigContext'
import { findConflict } from '@/utils/bindRegistry'

const fadeIn = keyframes`
    from { opacity: 0; backdrop-filter: blur(0px); }
    to { opacity: 1; backdrop-filter: blur(8px); }
`

const slideUp = keyframes`
    from { opacity: 0; transform: translate(-50%, -45%) scale(0.95); }
    to { opacity: 1; transform: translate(-50%, -50%) scale(1); }
`

const SelectorButton = styled.button`
    background: rgba(99, 102, 241, 0.05);
    border: 1px solid rgba(99, 102, 241, 0.15);
    color: rgba(99, 102, 241, 0.7);
    padding: 6px 12px;
    border-radius: 8px;
    min-width: 80px;
    font-family: 'Consolas', monospace;
    font-weight: 700;
    text-transform: uppercase;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    position: relative;
    overflow: hidden;

    &:hover {
        background: rgba(99, 102, 241, 0.1);
        border-color: rgba(99, 102, 241, 0.4);
        color: #6366f1;
    }

    ${({ $hasValue }) => $hasValue && `
        background: linear-gradient(135deg, rgba(99, 102, 241, 0.2), rgba(99, 102, 241, 0.1));
        border-color: rgba(99, 102, 241, 0.6);
        color: #a4e1ffff;
        box-shadow: 0 0 10px rgba(99, 102, 241, 0.3), 
                    inset 0 0 12px rgba(99, 102, 241, 0.1);
        text-shadow: 0 0 8px rgba(255, 255, 255, 0.3);
        font-weight: 800;
        letter-spacing: 0.5px;

        &:hover {
            background: linear-gradient(135deg, rgba(99, 102, 241, 0.25), rgba(99, 102, 241, 0.15));
            box-shadow: 0 0 25px rgba(99, 102, 241, 0.3), 
                        inset 0 0 15px rgba(99, 102, 241, 0.2);
            border-color: rgba(99, 102, 241, 0.8);
        }
    `}

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`

const FloatingPanel = styled.div`
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 10000;
    background: #0d1117;
    //background-image: linear-gradient(rgba(255, 255, 255, 0.03) 0px, transparent 1px), 
    //                  linear-gradient(90deg, rgba(255, 255, 255, 0.03) 0px, transparent 1px);
    background-size: 20px 20px;
    backdrop-filter: blur(25px);
    border: 1px solid rgba(255, 255, 255, 0.05);
    box-shadow: 0 40px 100px rgba(0, 0, 0, 0.9);
    border-radius: 16px;
    width: 980px;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    animation: ${slideUp} 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: inset 0px 36px 90px 46px #050208c9;
`

const MouseHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    background: rgba(0, 0, 0, 0.4);
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
    padding: 10px 24px;
    width: 100%;
`

const MouseLabel = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    color: #6366f1;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 2px;
    font-family: 'Consolas', monospace;
    opacity: 0.9;
    
    svg {
        font-size: 20px;
    }
`



const GridContent = styled.div`
    padding: 12px;
    max-height: 280px;
    overflow-y: auto;
    width: 100%;
    box-sizing: border-box;
`

const Grid = styled.div`
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 6px;
    width: 100%;
`

const KeyCap = styled.div`
    background: rgba(255, 255, 255, 0.03);
    border: 1px solid rgba(255, 255, 255, 0.15);
    color: #fff;
    height: 34px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 6px;
    font-size: 11px;
    font-family: 'Consolas', monospace;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.1s;
    text-transform: uppercase;
    text-align: center;
    box-shadow: 0 2px 4px rgba(0,0,0,0.2);

    &:hover {
        background: #6366f1;
        color: white;
        border-color: #6366f1;
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
    }

    ${({ active }) => active && `
        background: #6366f1;
        border-color: #6366f1;
        color: white;
        box-shadow: 0 0 15px rgba(99, 102, 241, 0.4);
    `}

    ${({ $colSpan }) => $colSpan && `grid-column: span ${$colSpan};`}
    ${({ $rowSpan }) => $rowSpan && `grid-row: span ${$rowSpan};`}
    ${({ $isGhost }) => $isGhost && `
        opacity: 0.3;
        pointer-events: none;
        background: transparent;
        border: 1px dashed rgba(255,255,255,0.1);
    `}
`

const Overlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 9999;
    background: rgba(0, 0, 0, 0.7);
    backdrop-filter: blur(4px);
    animation: ${fadeIn} 0.3s ease;
`

const Footer = styled.div`
    padding: 6px 12px;
    background: rgba(0, 0, 0, 0.2);
    border-top: 1px solid rgba(255, 255, 255, 0.05);
    font-size: 8px;
    color: #6366f1;
    opacity: 0.6;
    text-align: right;
    text-transform: uppercase;
    letter-spacing: 1px;
    font-weight: 800;
`

const LabelWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 6px;
`

const LabelText = styled.span`
    color: rgba(255, 255, 255, 0.85);
    font-size: 0.75rem;
    font-weight: 500;
    letter-spacing: 0.1px;
`

const HelpIconWrapper = styled.div`
    display: flex;
    align-items: center;
    color: rgba(255, 255, 255, 0.4);
    cursor: help;
    transition: color 0.2s;

    svg {
        font-size: 14px;
    }

    &:hover {
        color: #6366f1;
    }
`

const InputContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: ${({ $fullWidth }) => ($fullWidth ? '100%' : 'auto')};
    ${({ $hasLabel }) => $hasLabel && 'margin-bottom: 4px;'}
`


const ClearButton = styled.div`
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: rgba(255, 255, 255, 0.3);
    border-radius: 6px;
    cursor: pointer;
    font-size: 12px;
    opacity: 0;
    pointer-events: none;
    background-color: rgba(88, 22, 22, 1);
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    z-index: 20;

`

const ClearButtonContainer = styled.div`
    position: absolute;
    left: -8px;
    top: 50%;
    transform: translateY(-50%);
    //width: 36px;
    height: 24px;

    &:hover ${ClearButton} {
        background-color: rgba(161, 36, 36, 1) !important;
        color: #ffffffff;
        pointer-events: auto;
    }
`

const SelectorWrapper = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    width: ${({ $fullWidth }) => ($fullWidth ? '100%' : 'auto')};
    min-width: ${({ $hasLabel }) => ($hasLabel ? '0' : '80px')};

    &:hover ${ClearButton} {
        opacity: 1;
        pointer-events: auto;
    }
`

const ConflictOverlay = styled.div`
    position: absolute;
    inset: 0;
    z-index: 10;
    background: rgba(8, 5, 18, 0.92);
    backdrop-filter: blur(8px);
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 16px;
`

const ConflictBox = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 14px;
    padding: 36px 48px;
    max-width: 500px;
    width: 100%;
    text-align: center;
`

const ConflictKeyBadge = styled.div`
    font-family: 'Consolas', monospace;
    font-size: 24px;
    font-weight: 900;
    text-transform: uppercase;
    text-align: center;
    color: #f59e0b;
    background: rgba(245, 158, 11, 0.1);
    border: 1px solid rgba(245, 158, 11, 0.4);
    border-radius: 10px;
    padding: 8px 24px;
    letter-spacing: 3px;
    justify-content: center;
    margin-bottom: 20px;
`

const ConflictTitle = styled.div`
    font-size: 14px;
    font-weight: 700;
    color: rgba(255, 255, 255, 0.9);
    letter-spacing: 0.5px;
    text-align: center;
    justify-content: center;
`

const ConflictUsedIn = styled.div`
    font-size: 11px;
    color: rgba(255, 255, 255, 0.4);
    text-transform: uppercase;
    letter-spacing: 1.5px;
    text-align: center;
    justify-content: center;
`

const ConflictActionName = styled.div`
    font-size: 15px;
    font-weight: 700;
    color: #a78bfa;
    text-align: center;
`

const ConflictButtons = styled.div`
    display: flex;
    gap: 10px;
    margin-top: 6px;
    justify-content: center;
    width: 100%;
`

const ConflictBtn = styled.button`
    padding: 9px 28px;
    border-radius: 8px;
    font-size: 12px;
    font-weight: 700;
    font-family: 'Consolas', monospace;
    letter-spacing: 1px;
    cursor: pointer;
    transition: all 0.2s;
    border: 1px solid;
    flex: 1;
    max-width: 180px;

    ${({ $variant }) => $variant === 'replace' ? `
        background: rgba(239, 68, 68, 0.12);
        border-color: rgba(239, 68, 68, 0.45);
        color: #f87171;
        &:hover {
            background: rgba(239, 68, 68, 0.22);
            border-color: rgba(239, 68, 68, 0.8);
        }
    ` : `
        background: rgba(99, 102, 241, 0.08);
        border-color: rgba(99, 102, 241, 0.3);
        color: #818cf8;
        &:hover {
            background: rgba(99, 102, 241, 0.18);
            border-color: rgba(99, 102, 241, 0.6);
        }
    `}
`

const MOUSE_LAYOUT = [
    { key: 'mouse1', label: 'MOUSE 1', width: '90px' },
    { key: 'mouse2', label: 'MOUSE 2', width: '90px' },
    { key: 'mouse3', label: 'MOUSE 3', width: '90px' },
    { key: 'mouse4', label: 'M4', width: '50px' },
    { key: 'mouse5', label: 'M5', width: '50px' },
    { key: 'mwheelup', label: 'WH ▲', width: '60px' },
    { key: 'mwheeldown', label: 'WH ▼', width: '60px' }
]

const FULL_LAYOUT = [
    // --- ROW 0: F-Keys & System ---
    { key: 'escape', label: 'ESC', colSpan: 4 },
    { key: 'gh_f1', isGhost: true, colSpan: 4 }, // Gap
    { key: 'f1', colSpan: 4 }, { key: 'f2', colSpan: 4 }, { key: 'f3', colSpan: 4 }, { key: 'f4', colSpan: 4 },
    { key: 'gh_f2', isGhost: true, colSpan: 2 }, // Gap
    { key: 'f5', colSpan: 4 }, { key: 'f6', colSpan: 4 }, { key: 'f7', colSpan: 4 }, { key: 'f8', colSpan: 4 },
    { key: 'gh_f3', isGhost: true, colSpan: 2 }, // Gap
    { key: 'f9', colSpan: 4 }, { key: 'f10', colSpan: 4 }, { key: 'f11', colSpan: 4 }, { key: 'f12', colSpan: 4 },
    { key: 'gh_f4', isGhost: true, colSpan: 2 }, // Gap to Nav
    { key: 'prtscr', label: 'PRT', colSpan: 4 }, { key: 'scroll', label: 'SCR', colSpan: 4 }, { key: 'pause', label: 'PAU', colSpan: 4 },
    { key: 'gh_f5', isGhost: true, colSpan: 2 }, // Gap to Numpad
    { key: 'gh_f6', isGhost: true, colSpan: 16 }, // Empty space above numpad

    // --- ROW 1: Numbers & Mods ---
    // Main (60 cols)
    { key: '`', label: '`', colSpan: 4 },
    { key: '1', colSpan: 4 }, { key: '2', colSpan: 4 }, { key: '3', colSpan: 4 }, { key: '4', colSpan: 4 }, { key: '5', colSpan: 4 },
    { key: '6', colSpan: 4 }, { key: '7', colSpan: 4 }, { key: '8', colSpan: 4 }, { key: '9', colSpan: 4 }, { key: '0', colSpan: 4 },
    { key: '-', label: '-', colSpan: 4 }, { key: '=', label: '=', colSpan: 4 },
    { key: 'backspace', label: '⌫', colSpan: 8 },
    // Nav (12 cols)
    { key: 'gh_n1', isGhost: true, colSpan: 2 },
    { key: 'ins', label: 'INS', colSpan: 4 }, { key: 'home', label: 'HM', colSpan: 4 }, { key: 'pgup', label: 'PU', colSpan: 4 },
    // Numpad (16 cols)
    { key: 'gh_np1', isGhost: true, colSpan: 2 },
    { key: 'numlock', label: 'NUM', isGhost: true, colSpan: 4 }, { key: 'kp_slash', label: '/', colSpan: 4 }, { key: 'kp_multiply', label: '*', colSpan: 4 }, { key: 'kp_minus', label: '-', colSpan: 4 },

    // --- ROW 2: QWERTY ---
    // Main
    { key: 'tab', label: 'TAB', colSpan: 6 },
    { key: 'q', colSpan: 4 }, { key: 'w', colSpan: 4 }, { key: 'e', colSpan: 4 }, { key: 'r', colSpan: 4 }, { key: 't', colSpan: 4 },
    { key: 'y', colSpan: 4 }, { key: 'u', colSpan: 4 }, { key: 'i', colSpan: 4 }, { key: 'o', colSpan: 4 }, { key: 'p', colSpan: 4 },
    { key: '[', label: '[', colSpan: 4 }, { key: ']', label: ']', colSpan: 4 },
    { key: '\\', label: '\\', colSpan: 6 },
    // Nav
    { key: 'gh_n2', isGhost: true, colSpan: 2 },
    { key: 'del', label: 'DEL', colSpan: 4 }, { key: 'end', label: 'END', colSpan: 4 }, { key: 'pgdn', label: 'PD', colSpan: 4 },
    // Numpad
    { key: 'gh_np2', isGhost: true, colSpan: 2 },
    { key: 'kp_home', label: '7', colSpan: 4 }, { key: 'kp_uparrow', label: '8', colSpan: 4 }, { key: 'kp_pgup', label: '9', colSpan: 4 }, { key: 'kp_plus', label: '+', rowSpan: 2, colSpan: 4 },

    // --- ROW 3: ASDF ---
    // Main
    { key: 'capslock', label: 'CAPS', colSpan: 7 },
    { key: 'a', colSpan: 4 }, { key: 's', colSpan: 4 }, { key: 'd', colSpan: 4 }, { key: 'f', colSpan: 4 }, { key: 'g', colSpan: 4 },
    { key: 'h', colSpan: 4 }, { key: 'j', colSpan: 4 }, { key: 'k', colSpan: 4 }, { key: 'l', colSpan: 4 },
    { key: 'semicolon', label: ';', colSpan: 4 }, { key: "'", label: "'", colSpan: 4 },
    { key: 'enter', label: 'ENTER', colSpan: 9 },
    // Nav (Empty)
    { key: 'gh_n3', isGhost: true, colSpan: 14 },
    // Numpad
    { key: 'gh_np3', isGhost: true, colSpan: 2 },
    { key: 'kp_leftarrow', label: '4', colSpan: 4 }, { key: 'kp_5', label: '5', colSpan: 4 }, { key: 'kp_rightarrow', label: '6', colSpan: 4 },
    // Plus spanned from above

    // --- ROW 4: ZXCV ---
    // Main
    { key: 'shift', label: 'SHIFT', colSpan: 9 },
    { key: 'z', colSpan: 4 }, { key: 'x', colSpan: 4 }, { key: 'c', colSpan: 4 }, { key: 'v', colSpan: 4 }, { key: 'b', colSpan: 4 },
    { key: 'n', colSpan: 4 }, { key: 'm', colSpan: 4 },
    { key: ',', label: ',', colSpan: 4 }, { key: '.', label: '.', colSpan: 4 }, { key: '/', label: '/', colSpan: 4 },
    { key: 'rshift', label: 'SHIFT', colSpan: 11 },
    // Nav (Arrow Up)
    { key: 'gh_n4', isGhost: true, colSpan: 6 },
    { key: 'uparrow', label: '▲', colSpan: 4 },
    { key: 'gh_n42', isGhost: true, colSpan: 4 },
    // Numpad
    { key: 'gh_np4', isGhost: true, colSpan: 2 },
    { key: 'kp_end', label: '1', colSpan: 4 }, { key: 'kp_downarrow', label: '2', colSpan: 4 }, { key: 'kp_pgdn', label: '3', colSpan: 4 }, { key: 'kp_enter', label: 'ENT', rowSpan: 2, colSpan: 4 },

    // --- ROW 5: Bottom ---
    // Main
    { key: 'ctrl', label: 'CTRL', colSpan: 5 },
    { key: 'win', label: '❖', isGhost: true, colSpan: 5 },
    { key: 'alt', label: 'ALT', colSpan: 5 },
    { key: 'space', label: 'SPACE', colSpan: 25 },
    { key: 'ralt', label: 'RALT', colSpan: 5 },
    { key: 'win_r', label: '❖', isGhost: true, colSpan: 5 },
    { key: 'menu', label: '☰', isGhost: true, colSpan: 5 },
    { key: 'rctrl', label: 'RCTRL', colSpan: 5 },
    // Nav (Arrows)
    { key: 'gh_n5', isGhost: true, colSpan: 2 },
    { key: 'leftarrow', label: '◄', colSpan: 4 }, { key: 'downarrow', label: '▼', colSpan: 4 }, { key: 'rightarrow', label: '►', colSpan: 4 },
    // Numpad
    { key: 'gh_np5', isGhost: true, colSpan: 2 },
    { key: 'kp_ins', label: '0', colSpan: 8 }, { key: 'kp_del', label: '.', colSpan: 4 },
    // Enter spanned from above
]

// --- HELPER WRAPPER ---
const mapBrowserKeyToInternal = (e) => {
    const code = e.code

    // Letter keys: KeyW -> w
    if (code.startsWith('Key')) {
        return code.replace('Key', '').toLowerCase()
    }

    // Digit keys: Digit1 -> 1
    if (code.startsWith('Digit')) {
        return code.replace('Digit', '')
    }

    // Numpad keys
    const numpadMap = {
        'Numpad1': 'kp_end',
        'Numpad2': 'kp_downarrow',
        'Numpad3': 'kp_pgdn',
        'Numpad4': 'kp_leftarrow',
        'Numpad5': 'kp_5',
        'Numpad6': 'kp_rightarrow',
        'Numpad7': 'kp_home',
        'Numpad8': 'kp_uparrow',
        'Numpad9': 'kp_pgup',
        'Numpad0': 'kp_ins',
        'NumpadDecimal': 'kp_del',
        'NumpadEnter': 'kp_enter',
        'NumpadAdd': 'kp_plus',
        'NumpadSubtract': 'kp_minus',
        'NumpadMultiply': 'kp_multiply',
        'NumpadDivide': 'kp_slash',
    }
    if (numpadMap[code]) return numpadMap[code]

    // Special keys
    const specialMap = {
        'Escape': 'escape',
        'Backspace': 'backspace',
        'Tab': 'tab',
        'CapsLock': 'capslock',
        'Enter': 'enter',
        'ShiftLeft': 'shift',
        'ShiftRight': 'rshift',
        'ControlLeft': 'ctrl',
        'ControlRight': 'rctrl',
        'AltLeft': 'alt',
        'AltRight': 'ralt',
        'Space': 'space',
        'ArrowUp': 'uparrow',
        'ArrowDown': 'downarrow',
        'ArrowLeft': 'leftarrow',
        'ArrowRight': 'rightarrow',
        'Insert': 'ins',
        'Home': 'home',
        'PageUp': 'pgup',
        'Delete': 'del',
        'End': 'end',
        'PageDown': 'pgdn',
        'Semicolon': 'semicolon',
        'Quote': "'",
        'Comma': ',',
        'Period': '.',
        'Slash': '/',
        'Backslash': '\\',
        'BracketLeft': '[',
        'BracketRight': ']',
        'Backquote': '`',
        'Minus': '-',
        'Equal': '=',
        'PrintScreen': 'prtscr',
        'ScrollLock': 'scroll',
        'Pause': 'pause',
    }

    if (code.startsWith('F') && !isNaN(code.substring(1))) {
        return code.toLowerCase()
    }

    return specialMap[code] || null
}

const StandardGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(92, 1fr);
    grid-auto-rows: 32px;
    gap: 5px;
    width: 100%;
    margin: 0 auto;
`

const NumpadGrid = styled.div`
    display: none;
`

const UtilityGrid = styled.div`
    display: none;
`

export default function KeySelector({ value, onChange, disabled, label, description, fieldPath }) {
    const [isOpen, setIsOpen] = useState(false)
    const [conflictInfo, setConflictInfo] = useState(null) // { label, fieldPath } of the conflicting bind
    const [pendingKey, setPendingKey] = useState(null)

    const { configData, clearBind, allLauncherBinds, clearLauncherBindByKey } = useConfig()

    useEffect(() => {
        if (!isOpen) return

        const handleKeyDown = (e) => {
            // Permitir F12 para devtools
            if (e.key === 'F12') return

            e.preventDefault()
            e.stopPropagation()

            const pressedKey = mapBrowserKeyToInternal(e)

            if (pressedKey) {
                // Verificar si existe en nuestro layout y no es un ghost
                const found = FULL_LAYOUT.find(l => l.key === pressedKey) || MOUSE_LAYOUT.find(l => l.key === pressedKey)

                if (found && !found.isGhost) {
                    handleKeyClick(pressedKey)
                }
            }
        }

        window.addEventListener('keydown', handleKeyDown)
        return () => window.removeEventListener('keydown', handleKeyDown)
    }, [isOpen, conflictInfo])

    const toggleOpen = () => {
        if (disabled) return
        setIsOpen(!isOpen)
    }

    const handleKeyClick = (key) => {
        if (fieldPath) {
            const conflict = findConflict(configData, key, fieldPath)
            if (conflict) {
                setPendingKey(key)
                setConflictInfo(conflict)
                return
            }
        }
        if (allLauncherBinds && key !== value) {
            const conflict = allLauncherBinds.get(key)
            if (conflict) {
                setPendingKey(key)
                setConflictInfo({ ...conflict, _isLauncherBind: true })
                return
            }
        }
        commitKey(key)
    }

    const commitKey = (key) => {
        onChange(key)
        setConflictInfo(null)
        setPendingKey(null)
        setIsOpen(false)
    }

    const handleReplace = () => {
        if (conflictInfo._isLauncherBind) {
            clearLauncherBindByKey(pendingKey)
        } else {
            clearBind(conflictInfo.fieldPath)
        }
        commitKey(pendingKey)
    }

    const handleCancelConflict = () => {
        setConflictInfo(null)
        setPendingKey(null)
        // Panel stays open so the user can pick another key
    }

    return (
        <InputContainer $hasLabel={!!label} $fullWidth={!!label}>
            {label && (
                <LabelWrapper>
                    <LabelText>{label}</LabelText>
                    {description && (
                        <Tooltip title={description} arrow placement="top">
                            <HelpIconWrapper>
                                <HelpOutlineIcon />
                            </HelpIconWrapper>
                        </Tooltip>
                    )}
                </LabelWrapper>
            )}
            <SelectorWrapper $fullWidth={!!label} $hasLabel={!!label}>
                <SelectorButton
                    onClick={toggleOpen}
                    disabled={disabled}
                    type="button"
                    $hasValue={!!value}
                >
                    {value || 'NONE'}
                </SelectorButton>

                {value && !disabled && (
                    <ClearButtonContainer>

                        <ClearButton
                            onClick={(e) => {
                                e.stopPropagation()
                                onChange('')
                            }}
                            title="Clear bind"
                        >
                            ✕
                        </ClearButton>
                    </ClearButtonContainer>
                )}

                {isOpen && createPortal(
                    <>
                        <Overlay onClick={() => { setConflictInfo(null); setPendingKey(null); setIsOpen(false) }} />
                        <FloatingPanel>
                            {conflictInfo && (
                                <ConflictOverlay>
                                    <ConflictBox>
                                        <WarningAmberIcon style={{ fontSize: 40, color: '#f59e0b' }} />
                                        <ConflictTitle>Tecla ya asignada</ConflictTitle>
                                        <ConflictKeyBadge>{pendingKey}</ConflictKeyBadge>
                                        <ConflictUsedIn>Actualmente usada en</ConflictUsedIn>
                                        <ConflictActionName>{conflictInfo.label}</ConflictActionName>
                                        <ConflictButtons>
                                            <ConflictBtn $variant="replace" onClick={handleReplace}>
                                                Reemplazar
                                            </ConflictBtn>
                                            <ConflictBtn $variant="cancel" onClick={handleCancelConflict}>
                                                Elegir otra
                                            </ConflictBtn>
                                        </ConflictButtons>
                                    </ConflictBox>
                                </ConflictOverlay>
                            )}
                            <MouseHeader>
                                <MouseLabel>
                                    <MouseIcon />
                                    MOUSE INPUTS
                                </MouseLabel>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    {MOUSE_LAYOUT.map(mBtn => (
                                        <KeyCap
                                            key={mBtn.key}
                                            active={value === mBtn.key}
                                            onClick={() => handleKeyClick(mBtn.key)}
                                            style={{ width: mBtn.width, height: '34px', fontSize: '10px' }}
                                        >
                                            {mBtn.label}
                                        </KeyCap>
                                    ))}
                                </div>
                            </MouseHeader>
                            <GridContent>
                                <StandardGrid>
                                    {FULL_LAYOUT.map((btn, idx) => (
                                        <KeyCap
                                            key={btn.key + idx}
                                            active={value === btn.key}
                                            onClick={() => !btn.isGhost && handleKeyClick(btn.key)}
                                            $colSpan={btn.colSpan || 4} /* Default to 4 (1u) if not specified */
                                            $rowSpan={btn.rowSpan}
                                            $isGhost={btn.isGhost}
                                        >
                                            {!btn.isGhost && (btn.label || btn.key)}
                                        </KeyCap>
                                    ))}
                                </StandardGrid>
                            </GridContent>
                            <Footer>
                                CONFIG-STRIKE PROTOCOLS & BINDS
                            </Footer>
                        </FloatingPanel>
                    </>,
                    document.body
                )}
            </SelectorWrapper>
        </InputContainer>
    )
}
